// ?use_ios_ui — twin of iOS 0.7.0 JoystickPad: four press-and-hold arrow
// tiles round an empty centre tile (depth has its own slider). Repeats on
// iOS's RepeatButton timing: once on press, then every 50 ms after 200 ms.

import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { RepeatButton } from '@/components/repeat-button/repeat-button'
import { IOS_UI_PALETTE, panelTileStyle } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'

const SPACING_PX = 6

/** Width and height of the whole pad: three buttons and two gaps. */
export function joystickPadSide(buttonSize: number): number {
  return buttonSize * 3 + SPACING_PX * 2
}

interface IosJoystickPadProps {
  onUp: () => void
  onDown: () => void
  onLeft: () => void
  onRight: () => void
  buttonSize?: number
}

export function IosJoystickPad({ onUp, onDown, onLeft, onRight, buttonSize = 44 }: IosJoystickPadProps) {
  const arrow = (dir: string, onPress: () => void, icon: ReactNode) => (
    <PadButton key={dir} dir={dir} onPress={onPress} size={buttonSize}>
      {icon}
    </PadButton>
  )
  const glyph = { size: 15, strokeWidth: 2.5 }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACING_PX }}>
      {arrow('up', onUp, <ChevronUp {...glyph} />)}
      <div style={{ display: 'flex', gap: SPACING_PX }}>
        {arrow('left', onLeft, <ChevronLeft {...glyph} />)}
        <div
          data-testid="ios-pad-center"
          style={{ width: buttonSize, height: buttonSize, boxSizing: 'border-box', borderRadius: 12, border: `1px solid ${IOS_UI_PALETTE.tileStroke}` }}
        />
        {arrow('right', onRight, <ChevronRight {...glyph} />)}
      </div>
      {arrow('down', onDown, <ChevronDown {...glyph} />)}
    </div>
  )
}

function PadButton({ dir, onPress, size, children }: { dir: string; onPress: () => void; size: number; children: ReactNode }) {
  const [pressed, setPressed] = useState(false)
  return (
    <div
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{ width: size, height: size }}
    >
      <RepeatButton
        ariaLabel={`Move ${dir}`}
        testId={`ios-pad-${dir}`}
        onPress={onPress}
        initialDelayMs={200}
        repeatIntervalMs={50}
        style={{
          ...panelTileStyle(pressed),
          width: '100%',
          height: '100%',
          color: pressed ? IOS_UI_PALETTE.accentOnDark : 'rgba(255,255,255,0.85)',
        }}
      >
        {children}
      </RepeatButton>
    </div>
  )
}
